import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase, type PlanType } from "@/shared/db";
import {
  getPlansByUserType,
  getPlanConfig,
  type UserType,
} from "@/shared/config";
import { CheckoutSidebar } from "../components/CheckoutSidebar";
import { Button } from "@/shared/ui";
import { ROUTES } from "@/shared/config";
import { analytics } from "@/utils/analytics";
import { Check, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { motion } from "framer-motion";

export const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const isSpanish = i18n.language === "es";
  const [checkoutSidebarOpen, setCheckoutSidebarOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<"month" | "year">("month");
  const [currentPlan, setCurrentPlan] = useState<PlanType | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  // Load subscription directly using Supabase query (bypassing hanging RPC)
  useEffect(() => {
    const loadCurrentSubscription = async () => {
      if (!user?.id) {
        setLoadingSubscription(false);
        setCurrentPlan(null);
        return;
      }

      try {
        setLoadingSubscription(true);

        // Direct query to subscriptions table (bypassing RPC that hangs)
        const { data: sub, error } = await supabase
          .from("subscriptions")
          .select("plan_type, status")
          .eq("user_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        if (error) {
          console.error("❌ Error querying subscription:", error);
          setCurrentPlan(null);
        } else {
          if (sub?.plan_type) {
            setCurrentPlan(sub.plan_type as PlanType);
          } else {
            setCurrentPlan(null);
          }
        }
      } catch (error) {
        console.error("❌ Error loading subscription:", error);
        setCurrentPlan(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadCurrentSubscription();
  }, [user?.id]);

  // Track subscription page view
  useEffect(() => {
    if (user) {
      analytics.subscriptionViewed("pricing_page");
    }
  }, [user?.id]);

  // Determine which plans to show - lawyer plans only
  const availablePlans = useMemo(() => {
    const userType: UserType = (profile?.user_type as UserType) ?? "lawyer";
    const planTypes = getPlansByUserType(userType);
    return planTypes.map((type) => ({
      type,
      isPopular: getPlanConfig(type).isPopular ?? false,
    }));
  }, [profile?.user_type]);

  const handlePlanSelect = (planType: PlanType) => {
    if (!user) {
      navigate(ROUTES.AUTH);
      return;
    }
    // lawyer_free does not go through Stripe checkout
    if (planType === "lawyer_free") return;

    setSelectedPlan(planType);
    setCheckoutSidebarOpen(true);
  };

  const handleCheckoutSuccess = () => {
    setCheckoutSidebarOpen(false);
    // Refresh to show updated subscription
    window.location.reload();
  };

  const getDisplayPrice = (basePrice: number): number =>
    billingPeriod === "year" ? basePrice * 12 * 0.9 : basePrice;

  return (
    <div className="min-h-screen bg-linear-to-b from-romance-50/60 via-white to-white py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 md:mb-10"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-tiber-200 bg-white px-3 py-1 text-xs font-medium text-tiber-700 mb-4">
            <ShieldCheck className="w-4 h-4 text-atlantis-600" />
            {t("pricing.subtitle")}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-tiber-900 mb-4">
            {t("pricing.title")}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-tiber-600 max-w-3xl mx-auto">
            {t("pricing.description")}
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8 md:mb-10 flex justify-center"
        >
          <div className="inline-flex items-center rounded-xl border border-tiber-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setBillingPeriod("month")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                billingPeriod === "month"
                  ? "bg-tiber-900 text-white"
                  : "text-tiber-700 hover:bg-romance-50"
              }`}
            >
              {t("pricing.modal.monthly")}
            </button>
            <button
              onClick={() => setBillingPeriod("year")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                billingPeriod === "year"
                  ? "bg-tiber-900 text-white"
                  : "text-tiber-700 hover:bg-romance-50"
              }`}
            >
              {t("pricing.modal.yearly")}
            </button>
            <span className="ml-2 inline-flex items-center gap-1 rounded-lg bg-atlantis-100 text-atlantis-800 px-2.5 py-1 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              {t("pricing.modal.save10")}
            </span>
          </div>
        </motion.div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6 md:gap-8 mb-12">
          {loadingSubscription ? (
            <div className="col-span-full flex justify-center py-12">
              <div className="animate-spin h-10 w-10 border-2 border-atlantis-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            availablePlans.map(({ type, isPopular }, index) => {
              const plan = getPlanConfig(type);
              const Icon = plan.icon ?? Check;
              const planName = isSpanish ? plan.nameEs : plan.name;
              const planFeatures = isSpanish ? plan.featuresEs : plan.features;
              const isCurrentPlan =
                currentPlan === type ||
                (currentPlan === null && type === "lawyer_free");
              const displayPrice = getDisplayPrice(plan.price);
              const monthlyEquivalent =
                billingPeriod === "year" ? displayPrice / 12 : displayPrice;
              const isFreePlan = plan.price === 0;
              const isDowngradeToFree = Boolean(
                type === "lawyer_free" &&
                currentPlan &&
                currentPlan !== "lawyer_free",
              );

              return (
                <motion.div
                  key={type}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl overflow-hidden ${
                    isCurrentPlan
                      ? "ring-4 ring-atlantis-500/20"
                      : "border border-tiber-200"
                  }`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-linear-to-r from-tiber-500 to-tiber-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                        {t("pricing.mostPopular")}
                      </span>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div className="absolute top-4 right-4 z-10">
                      <span className="bg-atlantis-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                        {t("pricing.currentPlan")}
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div
                    className={`bg-linear-to-br ${plan.color} p-6 text-white`}
                  >
                    <div className="flex items-center justify-center mb-3">
                      <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Icon className="w-8 h-8" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-center mb-1">
                      {planName}
                    </h3>
                    <div className="text-center mb-2">
                      <span className="text-4xl font-bold">
                        {isFreePlan
                          ? t("pricing.free")
                          : `$${displayPrice.toFixed(2)}`}
                      </span>
                      {!isFreePlan && (
                        <span className="text-white/80 text-sm ml-2">
                          /
                          {billingPeriod === "month"
                            ? t("pricing.month")
                            : t("pricing.year")}
                        </span>
                      )}
                    </div>
                    {billingPeriod === "year" && !isFreePlan && (
                      <p className="text-center text-xs text-white/85">
                        ${monthlyEquivalent.toFixed(2)} / {t("pricing.month")}
                      </p>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="p-6">
                    <ul className="space-y-3 mb-6 md:min-h-[260px]">
                      {planFeatures.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-atlantis-600 shrink-0 mt-0.5 mr-3" />
                          <span className="text-sm text-tiber-700 leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      variant={isPopular ? "primary" : "secondary"}
                      size="lg"
                      className="w-full rounded-xl"
                      onClick={() => handlePlanSelect(type)}
                      disabled={isCurrentPlan || isDowngradeToFree}
                    >
                      {isCurrentPlan
                        ? t("pricing.currentPlan")
                        : isDowngradeToFree
                          ? t("pricing.free")
                          : user
                            ? t("pricing.selectPlan")
                            : t("pricing.startNow")}
                    </Button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 mx-auto"
        >
          <h2 className="text-3xl font-bold text-tiber-900 mb-8 text-center">
            {t("pricing.faq.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: t("pricing.faq.q1"),
                a: t("pricing.faq.a1"),
              },
              {
                q: t("pricing.faq.q2"),
                a: t("pricing.faq.a2"),
              },
              {
                q: t("pricing.faq.q3"),
                a: t("pricing.faq.a3"),
              },
              {
                q: t("pricing.faq.q4"),
                a: t("pricing.faq.a4"),
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg border border-tiber-200 p-6"
              >
                <h3 className="font-semibold text-tiber-900 mb-2">{faq.q}</h3>
                <p className="text-tiber-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 text-center"
          >
            <div className="bg-linear-to-r from-tiber-500 to-tiber-600 text-white rounded-2xl p-8 md:p-12 shadow-xl">
              <Zap className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-4">
                {t("pricing.cta.title")}
              </h3>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                {t("pricing.cta.description")}
              </p>
              <Button
                onClick={() => navigate(ROUTES.AUTH)}
                className="bg-white text-tiber-500 hover:bg-romance-50 font-semibold px-8 py-4 text-lg"
              >
                {t("pricing.cta.button")}
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Checkout Sidebar */}
      <CheckoutSidebar
        isOpen={checkoutSidebarOpen}
        onClose={() => setCheckoutSidebarOpen(false)}
        planType={selectedPlan}
        billingPeriod={billingPeriod}
        onSuccess={handleCheckoutSuccess}
      />
    </div>
  );
};
